<script lang="ts">
    import { getContext, onDestroy } from 'svelte'

    import { gettext as _ } from 'hydria/services/gettext'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'

    import Date from 'hydria/components/Date.svelte'
    import type { DomainHandler } from './domain'
    import PageList from 'hydria/components/PageList.svelte'
    import { fade } from 'svelte/transition'
    import type { IContact } from 'wdom/services/contacts'
    import Province from 'wdom/components/contacts/Province.svelte'
    import PostalCode from 'wdom/components/contacts/PostalCode.svelte'
    import Country from 'wdom/components/contacts/Country.svelte'
    import InternationalPhone from 'wdom/components/contacts/InternationalPhone.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let ready = $state(false)
    let loading = $state(false)
    let cHistory: any[] | undefined = $state()
    let nItems = $state(0)
    let skip = $state(0)

    const Domain = <DomainHandler>getContext('Domain')

    // const status = sderived(Domain.status, ($status) => $status)
    let status = $state()
    unsubs.push(
        Domain.status.subscribe(($status) => {
            status = $status
            if (status == 'ready') loadHistory()
            else cHistory = undefined
        }),
    )

    async function loadHistory() {
        loading = true

        try {
            const res = await simpleFetch('GET', `/json/domain/1/${Domain.idna}/contact-history`, { skip: skip, limit: 10 })

            cHistory = res.data.items
            nItems = res.data.total_items
        } catch {}
        loading = false
        if (!ready) ready = true
    }

    let shownEntryId: string | undefined = $state()
    let shownEntry: any = $state()
    let historyEntry: any = $state()

    async function showEntry(logId: string) {
        let r: SimpleFetchResult

        shownEntry = undefined
        for (const v of cHistory as any[]) {
            if (v.log_id == logId) {
                historyEntry = v
                break
            }
        }
        shownEntryId = logId

        try {
            r = await simpleFetch('GET', `/json/domain/1/${Domain.idna}/contact-history/${logId}`)

            if (shownEntryId) {
                // don’t load if the modal was closed while loading
                shownEntry = r.data.entry
            }
        } catch {}
    }

    function closeEntry() {
        shownEntryId = undefined
        shownEntry = undefined
        historyEntry = undefined
    }

    // ============================================= Affichage d’une motte de contacts
    function contactType(contact: IContact) {
        switch (contact.type) {
            case 'registrant':
                return $_('Registrant')
            case 'owner':
                return $_('Owner')
            case 'admin':
                return $_('Administrative')
            case 'billing':
                return $_('Billing')
            case 'tech':
                return $_('Technical')
        }
        return '–'
    }
</script>

<style>
    .modal-dialog {
        --bs-modal-width: 90vw;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape' && shownEntryId != null) {
            closeEntry()
        }
    }}
/>

{#if ready}
    <div class="card">
        <div class="card-title text-primary">
            <span>{$_('Historique des contacts')}</span>
        </div>
        <div class="card-body">
            <div class="table-responsive">
                <table class="table table-condensed table-striped fixed-no-overflow contact-history-table">
                    <thead>
                        <tr>
                            <th>{$_('Date')}</th>
                            <th>{$_('Source')}</th>
                            <th>{$_('Afficher')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#if cHistory}
                            {#each cHistory as entry}
                                <tr data-entry-id={entry.log_id}>
                                    <td><Date value={entry.stamp} format="h-table-s" server={true} /></td>
                                    <td>{entry.source}</td>
                                    <td
                                        ><button
                                            type="button"
                                            class="btn btn-sm btn-secondary"
                                            disabled={!!shownEntryId || loading}
                                            onclick={() => {
                                                showEntry(entry.log_id)
                                            }}>{$_('Afficher')}</button
                                        ></td
                                    >
                                </tr>
                            {/each}
                        {/if}
                    </tbody>
                </table>
            </div>
        </div>
        <div class="card-footer text-bg-light">
            <PageList
                {loading}
                {nItems}
                limit={10}
                {skip}
                setSkip={(e) => {
                    skip = e
                    loadHistory()
                }}
            />
        </div>
    </div>
{/if}

{#if shownEntryId != null}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>Contacts au <Date value={historyEntry.stamp} format="h-table-s" server={true} /></div>
                </div>
                <div class="modal-body card-contacts">
                    {#if shownEntry == null}
                        <div>
                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            Un moment…
                        </div>
                    {:else}
                        <div class="contact-container">
                            {#each shownEntry.document.contacts as cdata}
                                <div>
                                    <div>
                                        <div class="type">
                                            {contactType(cdata)}
                                            {#if cdata.cira && cdata.cira.id}
                                                <div class="cira-id">{cdata.cira.id}</div>
                                            {/if}
                                        </div>
                                        <div class="infos-contact">
                                            <div class="name"><span>{cdata.name}</span></div>
                                            {#if cdata.org}<div>{cdata.org}</div>{/if}
                                            <br />
                                            {#if cdata.email}<em>{cdata.email}</em>{/if}
                                            {#each cdata.street as street}
                                                <div>{street}</div>
                                            {/each}
                                            {#if cdata.city || cdata.sp || cdata.postal_code}
                                                <div>
                                                    {#if cdata.city}<span class="contact-right-space">{cdata.city}</span>{/if}
                                                    {#if cdata.sp}<span><Province contact={cdata} /> ({cdata.sp})</span>{/if}
                                                    <PostalCode contact={cdata} />
                                                </div>
                                            {/if}
                                            {#if cdata.country && cdata.country != 'CA'}
                                                <div><Country contact={cdata} /></div>
                                            {/if}
                                            {#if cdata.voice || cdata.fax}
                                                <br />
                                                {#if cdata.voice}<div><InternationalPhone phone={cdata.voice} /></div>{/if}
                                                {#if cdata.fax}<div><InternationalPhone phone={cdata.fax} /> <span class="text-muted">({$_('fax')})</span></div>{/if}
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                            {/each}
                        </div>
                    {/if}
                </div>
                <div class="modal-footer text-bg-light">
                    <button type="button" class="btn btn-outline-secondary" onclick={() => closeEntry()}>{$_('Cancel')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => closeEntry()} aria-hidden="true"></div>
    </div>
{/if}
